import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  FileBarChart,
  Calendar,
  Package,
  Menu,
  X,
  LogOut,
  UserCog,
  ChevronRight,
  ChevronLeft,
  RotateCcw,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import ErpHealthIndicator from '@/components/erp/ErpHealthIndicator';

export const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('ayur_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('ayur_sidebar_collapsed', String(next));
      } catch { }
      return next;
    });
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch {
      navigate('/login');
    }
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', exact: true },
    { icon: Users, label: 'Patients', path: '/admin/patients' },
    { icon: FileText, label: 'Prescriptions', path: '/admin/prescriptions' },
    { icon: Package, label: 'Medicine Inventory', path: '/admin/inventory' },
    { icon: Calendar, label: 'Follow-ups', path: '/admin/followups' },
    { icon: FileBarChart, label: 'Reports', path: '/admin/reports' },
    { icon: UserCog, label: 'User Management', path: '/admin/users' },
    { icon: RotateCcw, label: 'ERP Recovery', path: '/admin/recovery' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ];

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* ── Top Navigation Bar ── */}
      <header className="bg-card border-b border-border sticky top-0 z-40 safe-top">
        <div className="flex items-center justify-between h-14 md:h-16 px-3 md:px-6">
          {/* Left: hamburger + collapse button + logo */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            {/* Mobile hamburger menu */}
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 flex-shrink-0 md:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            <Link to="/admin" className="flex items-center gap-2 min-w-0">
              <img
                src="/logo.png"
                alt="Sanjivani Ayurvedic Clinic"
                className="h-9 md:h-12 w-auto object-contain flex-shrink-0"
              />
              <span className="hidden sm:block text-sm font-medium text-muted-foreground truncate">
                Admin Portal
              </span>
            </Link>
          </div>

          {/* Right: notifications + user info + logout */}
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <NotificationDropdown />
            <div className="hidden md:flex flex-col items-end">
              <span className="text-sm font-medium leading-tight">{user?.name || 'Dr. Admin'}</span>
              <span className="text-xs text-muted-foreground">
                Clinic Administrator
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              title="Log out"
              className="h-10 w-10"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ── Mobile overlay ── */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-foreground/30 z-20 md:hidden backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* ── Sidebar ── */}
        <aside
          className={cn(
            "fixed top-0 left-0 z-30 h-full bg-card border-r border-border",
            "transition-all duration-300 ease-in-out overflow-y-auto flex flex-col",
            "md:sticky md:top-0",
            isCollapsed ? "w-72 md:w-16" : "w-72 md:w-64",
            sidebarOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full md:translate-x-0"
          )}
          style={{ height: sidebarOpen ? '100dvh' : undefined }}
        >
          {/* Sidebar header on mobile */}
          <div className="flex items-center justify-between p-4 border-b border-border md:hidden">
            <div className="flex flex-col">
              <span className="font-semibold text-sm">{user?.name || 'Dr. Admin'}</span>
              <span className="text-xs text-muted-foreground">Clinic Administrator</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Sidebar header on desktop (collapsible trigger) */}
          <div
            className={cn(
              "hidden md:flex items-center border-b border-border/40 py-2",
              isCollapsed ? "justify-center px-1" : "justify-between px-3"
            )}
          >
            {!isCollapsed && (
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                Navigation
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={toggleSidebarCollapse}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="sidebar-inner-toggle"
            >
              {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>

          <nav className={cn("space-y-0.5 flex-1", isCollapsed ? "p-2" : "p-3")}>
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 md:py-1.5 md:hidden">
              Navigation
            </p>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  title={item.label}
                  className={cn(
                    "flex items-center rounded-lg transition-colors text-sm font-medium",
                    "min-h-[44px] md:min-h-0",
                    isCollapsed
                      ? "md:justify-center md:px-2 md:py-2.5 px-3 py-3 gap-3"
                      : "gap-3 px-3 py-3 md:py-2.5",
                    active
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "hover:bg-muted text-foreground"
                  )}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className={cn("flex-1 truncate", isCollapsed && "md:hidden")}>
                    {item.label}
                  </span>
                  {active && <ChevronRight className="h-3 w-3 opacity-60 md:hidden" />}
                </Link>
              );
            })}
          </nav>

          {/* Sidebar footer: ERP Health Status + Mobile Logout */}
          <div className={cn("border-t border-border mt-auto", isCollapsed ? "p-2" : "p-3 space-y-2")}>
            <div className={cn(isCollapsed && "md:hidden")}>
              <ErpHealthIndicator />
            </div>
            {isCollapsed && (
              <div
                className="hidden md:flex justify-center py-1 cursor-pointer"
                onClick={toggleSidebarCollapse}
                title="Expand sidebar to view ERP Health"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
              </div>
            )}
            <div className="md:hidden pt-1">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors min-h-[44px]"
              >
                <LogOut className="h-4 w-4 flex-shrink-0" />
                Sign Out
              </button>
            </div>
          </div>
        </aside>

        {/* ── Main Content ── */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-7xl mx-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
